import * as v from 'valibot';

// SpecRef: ARCH-005
// Reference: docs/decisions/ARCH-005-ui-error-detection-resilience.md#9-schema-level-empty-data-detection

// Every issue here is a `@WARNING`, and that is a decision rather than laziness.
// `validateAndTransformResponse` drops ERROR-severity ITEMS out of an array
// `data`; this endpoint's `data` is one object, so nothing can be dropped and an
// ERROR would only mark the whole library invalid while still handing it on.
// One malformed subdivision must not cost the map its region tints. The
// selectors (`lib/geoLibrarySelectors.ts`) are what actually skip a row they
// cannot use, and they are unit-tested for it.
const WARN = '@WARNING:';

const serviceRegionSchema = v.object({
  id: v.string(`${WARN} Service region id is missing`),
  name: v.string(`${WARN} Service region name is missing`),
});

const geoCountrySchema = v.object({
  name: v.string(`${WARN} Country name is missing`),
  alpha2: v.string(`${WARN} Country alpha-2 is missing`),
  alpha3: v.string(`${WARN} Country alpha-3 is missing`),
  numericCode: v.number(`${WARN} Country numeric code is missing`),
  serviceRegionId: v.string(`${WARN} Country service region id is missing`),
  currencyCode: v.string(`${WARN} Country currency code is missing`),
});

const geoSubdivisionSchema = v.object({
  code: v.string(`${WARN} Subdivision code is missing`),
  shortCode: v.string(`${WARN} Subdivision short code is missing`),
  countryAlpha2: v.string(`${WARN} Subdivision country alpha-2 is missing`),
  name: v.string(`${WARN} Subdivision name is missing`),
  type: v.string(`${WARN} Subdivision type is missing`),
  hereMapsAlias: v.nullable(v.string()),
});

// `currencies` is on the wire and intentionally unmodelled — valibot ignores
// unknown keys, so a change to it can never fail this parse.
export const geoLibrarySchema = v.object({
  serviceRegions: v.pipe(
    v.array(serviceRegionSchema),
    v.check(
      regions => regions.length > 0,
      `${WARN} No service regions returned — the map will draw no served territory`,
    ),
  ),
  countries: v.array(geoCountrySchema),
  subdivisions: v.array(geoSubdivisionSchema),
});

export const geoLibraryResponseSchema = v.object({
  data: geoLibrarySchema,
  dataGeneratedAt: v.nullable(v.string()),
  errors: v.nullable(v.array(v.unknown())),
  expires: v.nullable(v.string()),
  errorsGeneratedAt: v.nullable(v.string()),
});

export type GeoLibraryResponse = v.InferInput<typeof geoLibraryResponseSchema>;
